/**
 * RTK Query endpoints — Public auth flows
 *
 * Migrated from:
 *   src/pages/accept-invitation/actions.js
 *   src/pages/reset-2fa/actions.js
 *   src/pages/reset-2fa-accept/actions.js
 *   src/pages/reset-password-accept/actions.js
 *
 * All four endpoints are public — no Bearer token, no X-Tenant-Id. Set
 * `extraOptions: { skipAuth: true, skipTenant: true }` so prepareHeaders
 * leaves them off.
 *
 * `acceptInvitation` and `resetPasswordAccept` can return a fresh session
 * (auto-login). The matching `onQueryStarted` blocks dispatch the legacy
 * AUTH_LOGIN_SUCCESS action and write tokens to localStorage so the rest
 * of the app sees the new user as logged in.
 */

import { baseApi } from './baseApi';
import { setSession } from '../../session/actions';

const AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS';

function persistAutoLogin(data, dispatch) {
  if (!data?.user || !data?.token) return;
  // Use setSession so sessionMiddleware persists to session_current in
  // localStorage and sessionReducer updates session.current. Raw individual
  // localStorage keys (token/refreshToken/tenant) are not read by the current
  // session system and would be lost on page reload.
  dispatch(
    setSession({
      user: data.user,
      token: data.token,
      refreshToken: data.refreshToken ?? null,
      tenant: data.tenant ?? null,
    })
  );
  // AUTH_LOGIN_SUCCESS updates loginReducer.current.user (needed for
  // hasPermission selectors). Include refreshToken so sessionReducer's
  // AUTH_LOGIN_SUCCESS case doesn't overwrite the value set above with undefined.
  dispatch({
    type: AUTH_LOGIN_SUCCESS,
    payload: {
      user: data.user,
      token: data.token,
      refreshToken: data.refreshToken ?? null,
      tenant: data.tenant ?? null,
    },
  });
}

const publicAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    acceptInvitation: builder.mutation({
      query: ({ token, email, password }) => ({
        url: '/auth/accept-invitation',
        method: 'POST',
        body: { token, email, password },
      }),
      extraOptions: { skipAuth: true, skipTenant: true },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          persistAutoLogin(data, dispatch);
        } catch {
          // surfaced via mutation error state
        }
      },
    }),

    reset2FA: builder.mutation({
      query: ({ email }) => ({
        url: '/su/auth/reset-2fa',
        method: 'POST',
        body: { email },
      }),
      extraOptions: { skipAuth: true, skipTenant: true },
    }),

    reset2FAAccept: builder.mutation({
      query: ({ token, email, password }) => ({
        url: '/su/auth/reset-2fa-accept',
        method: 'POST',
        body: { token, email, password },
      }),
      extraOptions: { skipAuth: true, skipTenant: true },
    }),

    resetPasswordAccept: builder.mutation({
      query: ({ token, email, password }) => ({
        url: '/auth/reset-password-accept',
        method: 'POST',
        body: { token, email, password },
      }),
      extraOptions: { skipAuth: true, skipTenant: true },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          persistAutoLogin(data, dispatch);
        } catch {
          // surfaced via mutation error state
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useAcceptInvitationMutation,
  useReset2FAMutation,
  useReset2FAAcceptMutation,
  useResetPasswordAcceptMutation,
} = publicAuthApi;

export default publicAuthApi;
